/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import * as React from "react";
// import { IndeterminateProgressBar, Panel } from '@partnercenter-react/ui-webcore';
import { LocalizationContext } from "../../context/LocalizationContext";
import { LocalizationInfo } from "@emt/emt-localization/lib/localization-info";
import { EnrollmentDetail } from "../../containers/base-container";
import {
  splitTextIntoMultipleLines,
  transformToClickableLink,
} from "../helper";
import button from "@harmony/enablers/components/button/button";
import { createScope } from "@harmony/enablers/react/scope-for-react";
import partnerCenterTheme from "@harmony/enablers/themes/partner-center";
import flyInPanel from "@harmony/enablers/components/fly-in-panel/fly-in-panel";
import messageBar from "@harmony/enablers/components/message-bar/message-bar";
import progress from "@harmony/enablers/components/progress/progress";
import text from "@harmony/enablers/components/text/text";
import icon from "@harmony/enablers/components/icon/icon";
require("./enrollment-agreement-modal.css");

export interface EnrollmentAgreementPanelProps {
  visible: boolean;
  isLoading: boolean;
  invitationDetail: EnrollmentDetail;
  startEnrollmentApiError: boolean;
  isLoadingBankAndTaxInfo: boolean;
}

export interface EnrollmentAgreementPanelCallbackProps {
  onClose: () => void;
  onContinue: () => void;
}

const scope = createScope({
  reactInstance: React,
  styles: [partnerCenterTheme],
});

const Progress = scope.forReact(progress);
const FlyInPanel = scope.forReact(flyInPanel);
const Button = scope.forReact(button);
const MessageBar = scope.forReact(messageBar);
const Text = scope.forReact(text);
const Icon = scope.forReact(icon);

class EnrollmentAgreementPanelState {
  enrollmentContinueButtonDisabled: boolean = false;
}

type EnrollmentAgreementPanelPropsType = EnrollmentAgreementPanelProps &
  EnrollmentAgreementPanelCallbackProps;

export class EnrollmentAgreementPanel extends React.Component<
  EnrollmentAgreementPanelPropsType,
  EnrollmentAgreementPanelState
> {
  private programNameNoSpace: string = "";
  private firstFocusElement = React.createRef<HTMLAnchorElement>();

  constructor(props: EnrollmentAgreementPanelPropsType) {
    super(props);
    this.state = new EnrollmentAgreementPanelState();
  }

  componentDidMount() {
    if (this.firstFocusElement.current) {
      this.firstFocusElement.current.focus();
    }
  }

  render(): JSX.Element {
    return (
      <LocalizationContext.Consumer>
        {(localization) => this.renderContent(localization)}
      </LocalizationContext.Consumer>
    );
  }

  private renderContent(localization: LocalizationInfo) {
    if (!this.props.invitationDetail) {
      return null;
    }

    this.programNameNoSpace = this.props.invitationDetail.programName.replace(
      /\s+/g,
      ""
    );

    return (
      <FlyInPanel
        style={{ "--size": "var(--he-panel-size-small)" }}
        open={this.props.visible}
        onHeAfterHide={this.props.onClose}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 10,
            justifyContent: "space-around",
            marginTop: 20,
            marginBottom: 50,
          }}
        >
          <h1>
            <label className="form-label">
              {localization.format(
                "EnrollInHeading",
                this.props.invitationDetail.programName
              )}
            </label>
          </h1>
          <Button appearance="stealth" onClick={() => this.props.onClose()}>
            <Icon name="cancel" label="Cancel"></Icon>
          </Button>
        </div>
        <div>
          {this.props.isLoadingBankAndTaxInfo && (
            <>
              <Progress label="Progress" indeterminate />
            </>
          )}
          <p>
            <>
              <div>
                {this.props.invitationDetail.locationName} (
                {this.props.invitationDetail.partnerId.type}:{" "}
                {this.props.invitationDetail
                  ? this.props.invitationDetail.partnerId.value
                  : ""}
                )<br />
                {this.props.invitationDetail.invitationOfferingTimeline
                  ? this.props.invitationDetail.invitationOfferingTimeline.name
                  : ""}
              </div>

              <div className="spacer-ml-top">
                <h2>
                  <label className="form-label">
                    {localization.strings["ProgramSummaryText"]}
                  </label>
                </h2>
              </div>

              <div className="spacer-xs-top">
                {(
                  this.props.invitationDetail.programDescriptionDetails || []
                ).map((descriptionDetail, index) => {
                  return (
                    <div className="spacer-xxs-top" key={index}>
                      <h3>
                        {" "}
                        <label>
                          {descriptionDetail ? descriptionDetail.heading : ""}
                        </label>
                      </h3>
                      <br />

                      <div className="spacer-xxs-top">
                        {descriptionDetail
                          ? splitTextIntoMultipleLines(
                              descriptionDetail.text,
                              "\n"
                            )
                          : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          </p>
        </div>
        <div
          style={{
            textAlign: "start",
            position: "relative",
            bottom: 0,
            zIndex: 3,
          }}
        >
          <div>
            <Text appearance={"small"}>
              {" "}
              {transformToClickableLink(
                localization.strings["EnrollmentAgreementModalFootnote"],
                "https://go.microsoft.com/fwlink/?linkid=874675"
              )}{" "}
            </Text>
            <div className="spacer-xs-top" style={{ display: "flex", gap: 10 }}>
              <Button
                type="submit"
                disabled={this.state.enrollmentContinueButtonDisabled}
                slot="actions"
                appearance="primary"
                onClick={() => {
                  if (this.state.enrollmentContinueButtonDisabled) {
                    return;
                  }
                  this.setState({ enrollmentContinueButtonDisabled: true });
                  this.props.onContinue();
                }}
                data-bi-slot="1"
                data-bi-id={"AgreementModalContinue-" + this.programNameNoSpace}
              >
                {localization.strings["Accept"]}
              </Button>
              <Button
                type="reset"
                appearance={"secondary"}
                onClick={() => this.props.onClose()}
                data-bi-slot="2"
                data-bi-id={"AgreementModalClose-" + this.programNameNoSpace}
              >
                {localization.strings["CancelModalText"]}
              </Button>
            </div>
          </div>

          <div className="win-color-fg-red spacer-md-large">
            {this.props.startEnrollmentApiError && (
              <MessageBar appearance="error" open>
                {
                  <>
                    <div>
                      <span>
                        {localization.strings["ErrorFetchingDataMessage"]}
                        &nbsp;
                      </span>
                      <a
                        className="win-color-fg-red underline"
                        onClick={() => location.reload()}
                      >
                        {localization.strings["TryAgainMessage"]}
                      </a>
                    </div>
                  </>
                }
              </MessageBar>
            )}
          </div>
        </div>{" "}
      </FlyInPanel>
    );
  }
}
